import { useCallback, useEffect, useRef, useState } from 'react'
import { deliveredCashIn, getCashDue, type CashDue, type CashDueRow } from '../lib/day-api'
import { isTillToday } from '../lib/orders-list'
import { useSilentRefresh } from '../lib/useSilentRefresh'
import { tr, type Lang } from '../lib/i18n'
import K from '../locales/cash-in.json'
import C from '../locales/collect.json'
import F from '../locales/final-check.json'

// R572 (b2), "Delivered, cash in" on the Orders list (Rook #69053, #69134): the cards come from
// (b0)'s GET /admin/till/cash-due. Out for delivery is listed whatever the day, and an order placed
// before today (Beirut) says so; delivered with no record is today's only (the server's bound).
// One tap posts POST /orders/:id/delivered-cash-in; a refusal shows the server's own words; an
// order whose cash this login already recorded and still out for delivery offers the retry,
// which only closes the delivery.

type RowState = { kind: 'idle' } | { kind: 'pending' } | { kind: 'refused'; message: string }

type Notice = { kind: 'recorded'; code: string; amount: string } | { kind: 'closed'; code: string }

const ALERT = 'rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3'

export function useCashDue(isOnline: boolean) {
  const [due, setDue] = useState<CashDue | null>(null)
  const [failed, setFailed] = useState(false)
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [notice, setNotice] = useState<Notice | null>(null)
  const inFlight = useRef(false)

  const load = useCallback(async (silent: boolean = false) => {
    if (inFlight.current) return
    inFlight.current = true
    if (!silent) setFailed(false)
    const r = await getCashDue()
    inFlight.current = false
    if (r.ok) {
      setDue(r.value)
      setFailed(false)
    } else if (!silent) {
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useSilentRefresh(() => {
    if (Object.values(rows).some((s) => s.kind === 'pending')) return
    void load(true)
  })

  async function cashIn(row: CashDueRow, lang: Lang) {
    if ((rows[row.id] ?? { kind: 'idle' }).kind === 'pending') return
    if (!isOnline) {
      setRows((prev) => ({ ...prev, [row.id]: { kind: 'refused', message: tr(K.needConnection, lang) } }))
      return
    }
    setRows((prev) => ({ ...prev, [row.id]: { kind: 'pending' } }))
    const r = await deliveredCashIn(row.id)
    if (!r.ok) {
      const message = r.kind === 'refused' ? r.message : tr(r.kind === 'offline' ? K.needConnection : K.network, lang)
      setRows((prev) => ({ ...prev, [row.id]: { kind: 'refused', message } }))
      return
    }
    setRows((prev) => ({ ...prev, [row.id]: { kind: 'idle' } }))
    setNotice(
      r.value.case === 'confirm_only'
        ? { kind: 'closed', code: r.value.code }
        : { kind: 'recorded', code: r.value.code, amount: r.value.amountUsd ?? row.totalAmount },
    )
    setDue(
      (prev) =>
        prev && {
          ...prev,
          outForDelivery: prev.outForDelivery.filter((o) => o.id !== row.id),
          deliveredNoRecord: prev.deliveredNoRecord.filter((o) => o.id !== row.id),
        },
    )
    void load(true)
  }

  return { due, failed, load, rows, notice, cashIn }
}

export type CashDueState = ReturnType<typeof useCashDue>

export function CashInNotice({ cd, lang }: { cd: CashDueState; lang: Lang }) {
  if (!cd.notice) return null
  return (
    <p id="cash-in-notice" role="status" className="rounded-lg bg-jade-100 text-jade-700 text-sm font-semibold px-4 py-3">
      {cd.notice.kind === 'closed'
        ? tr(K.cashInClosed, lang, { code: cd.notice.code })
        : tr(K.cashInDone, lang, { code: cd.notice.code, amount: cd.notice.amount })}
    </p>
  )
}

export function CashDueLoadError({ cd, lang }: { cd: CashDueState; lang: Lang }) {
  if (!cd.failed) return null
  return (
    <div className="space-y-3">
      <div id="cash-due-load-error" role="alert" className={ALERT}>
        {tr(K.cashDueLoadFailed, lang)}
      </div>
      <button
        id="cash-due-retry-btn"
        type="button"
        onClick={() => void cd.load()}
        className="w-full min-h-tap rounded-lg bg-fleet-700 hover:bg-fleet-800 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
      >
        {tr(F.tryAgain, lang)}
      </button>
    </div>
  )
}

/** One cash-due order: code, the person, the cash due, the driver, its age, a refusal. */
export function CashInBody({ row, cd, lang, now }: { row: CashDueRow; cd: CashDueState; lang: Lang; now: Date }) {
  const state = cd.rows[row.id] ?? { kind: 'idle' }
  const old = !isTillToday(row.placedAt, now)
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span dir="ltr" className="font-semibold">
          {row.code}
        </span>
        <span dir="ltr" className="text-lg font-bold text-navy tabular-nums">
          ${row.totalAmount}
        </span>
      </div>
      <p className="text-sm text-fleet-600">{row.customerName ?? tr(C.unknownCustomer, lang)}</p>
      {row.driverName && <p className="text-sm text-fleet-600">{tr(K.driverLine, lang, { name: row.driverName })}</p>}
      {old && (
        <span data-from-yesterday className="inline-block rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5">
          {tr(K.fromYesterday, lang)}
        </span>
      )}
      {state.kind === 'refused' && (
        <p role="alert" className={ALERT}>
          {state.message}
        </p>
      )}
    </>
  )
}

/** The card's one action: "Delivered, cash in", or the retry for this login's own stuck record. */
export function CashInButton({ row, cd, lang }: { row: CashDueRow; cd: CashDueState; lang: Lang }) {
  const pending = (cd.rows[row.id] ?? { kind: 'idle' }).kind === 'pending'
  return (
    <button
      type="button"
      data-cash-in-btn
      disabled={pending}
      aria-busy={pending}
      onClick={() => void cd.cashIn(row, lang)}
      className="w-full min-h-tap rounded-lg bg-navy hover:bg-navy/90 disabled:bg-fleet-300 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
    >
      {pending ? tr(K.cashInSending, lang) : row.hasCashRecord ? tr(K.cashInRetry, lang) : tr(K.cashIn, lang)}
    </button>
  )
}
